import { cs } from '../../../lib/currency';
/**
 * Edit Enrollment Dialog (iter271 — extracted from Enrollments.js, iter273
 * — added discount + final-price override support that matches the Quick
 * Enroll / Approve dialogs).
 *
 * Lets admin/staff tweak monthly fee, payment frequency, billing dates,
 * last-payment audit, proration AND now apply a catalog discount or free-
 * form discount/override on an active or paused enrollment. The new fee
 * is reflected in payment details (next invoice + recurring cycle) and a
 * `discount_usage` audit row is written on the backend.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Pencil, DollarSign, Check } from 'lucide-react';

export default function EditEnrollmentDialog({
  open,
  onOpenChange,
  selectedEnrollment,
  editData,
  setEditData,
  paymentFrequencies,
  discounts = [],
  programs = [],
  onConfirm,
}) {
  // ----- Final-price preview (mirrors Quick Enroll). -------------------------
  // Computes what the parent will be billed per cycle after applying the
  // discount / override the admin is currently picking. This is purely
  // presentational — the backend re-runs the exact same calculation as
  // the source of truth.
  const renderFinalPricePreview = () => {
    if (!selectedEnrollment) return null;
    const freq = paymentFrequencies.find(
      (f) => f.value === (editData.payment_frequency || 'monthly'),
    ) || paymentFrequencies[0];
    const program = programs.find((p) => p.id === selectedEnrollment.program_id);
    const editedPerMonth = parseFloat(editData.monthly_fee);
    const fallbackPerMonth = parseFloat(
      selectedEnrollment.original_monthly_fee
      ?? selectedEnrollment.monthly_fee
      ?? program?.[freq?.priceKey]
      ?? program?.price
      ?? 0,
    ) || 0;
    const perMonth = Number.isFinite(editedPerMonth) && editedPerMonth > 0
      ? editedPerMonth
      : fallbackPerMonth;
    const basePerCycle = perMonth * (freq?.multiplier || 1);

    let computed = basePerCycle;
    let pathLabel = freq?.value === 'monthly'
      ? `Base (${cs()}${basePerCycle.toFixed(2)}/mo)`
      : `${cs()}${perMonth.toFixed(2)}/mo × ${freq?.multiplier} = ${cs()}${basePerCycle.toFixed(2)}`;

    const selectedDiscount = (discounts || []).find((d) => d.id === editData.discount_id);
    if (selectedDiscount) {
      const dv = parseFloat(
        selectedDiscount.value !== undefined && selectedDiscount.value !== null
          ? selectedDiscount.value
          : (selectedDiscount.discount_value || 0),
      ) || 0;
      const dollarOff = selectedDiscount.discount_type === 'percentage'
        ? (basePerCycle * dv) / 100
        : dv;
      computed = Math.max(0, basePerCycle - dollarOff);
      pathLabel = `${selectedDiscount.name} → −${cs()}${dollarOff.toFixed(2)}`;
    } else if (editData.discount_amount && parseFloat(editData.discount_amount) > 0) {
      const dv = parseFloat(editData.discount_amount);
      computed = Math.max(0, basePerCycle - dv);
      pathLabel = `Custom discount −${cs()}${dv.toFixed(2)}`;
    }
    const overrideRaw = editData.final_monthly_fee;
    const overrideNum = overrideRaw !== '' && overrideRaw !== null && overrideRaw !== undefined
      ? parseFloat(overrideRaw)
      : null;
    if (overrideNum !== null && !Number.isNaN(overrideNum)) {
      computed = Math.max(0, overrideNum);
      pathLabel = `Override → ${cs()}${overrideNum.toFixed(2)} (wins)`;
    }

    const cycleSuffix = freq?.value === 'monthly' ? 'month'
      : freq?.value === 'quarterly' ? 'quarter'
        : freq?.value === 'biannual' ? '6mo' : freq?.value;
    return (
      <div
        className="text-xs px-3 py-2 rounded-md border bg-emerald-50 border-emerald-300 text-emerald-900"
        data-testid="edit-final-price-preview"
      >
        <div className="font-medium">
          Final: {cs()}{computed.toFixed(2)} / {cycleSuffix}
        </div>
        <div className="text-emerald-700/80">{pathLabel}</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Pencil className="h-5 w-5 text-blue-500" />
            Edit Enrollment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
            <p className="text-sm text-slate-500">Program: {selectedEnrollment?.program_name}</p>
            {selectedEnrollment?.class_names && selectedEnrollment.class_names.length > 0 && (
              <p className="text-sm text-slate-500">Classes: {selectedEnrollment.class_names.join(', ')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                Monthly Fee ($)
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editData.monthly_fee}
                onChange={(e) => setEditData({ ...editData, monthly_fee: e.target.value })}
                placeholder="Enter fee amount"
                data-testid="edit-fee-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Frequency</Label>
              <Select
                value={editData.payment_frequency}
                onValueChange={(value) => setEditData({ ...editData, payment_frequency: value })}
              >
                <SelectTrigger data-testid="edit-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]" sideOffset={4}>
                  {paymentFrequencies.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* iter273: Discount + final-price override section (matches Quick
              Enroll / Approve flows). All three knobs feed the backend
              which re-runs the same calculation, writes a discount_usage
              audit row, and reflects the new fee on the next invoice +
              recurring cycle. */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Discount &amp; price override</span>
              <span className="text-xs text-purple-500">(optional)</span>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Catalog discount</Label>
              <Select
                value={editData.discount_id || 'none'}
                onValueChange={(value) => {
                  const v = value === 'none' ? '' : value;
                  setEditData({
                    ...editData,
                    discount_id: v,
                    // Clearing the catalog selection should not also clear
                    // any manual amount the admin already typed.
                    discount_amount: v ? '' : editData.discount_amount,
                    discount_reason: v ? '' : editData.discount_reason,
                  });
                }}
              >
                <SelectTrigger className="h-9" data-testid="edit-discount-select">
                  <SelectValue placeholder="Select a discount" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]" sideOffset={4}>
                  <SelectItem value="none">No discount</SelectItem>
                  {(discounts || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} ({d.value ?? d.discount_value}{d.discount_type === 'percentage' ? '%' : '$'} off)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Custom discount ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.discount_amount ?? ''}
                  disabled={!!editData.discount_id}
                  onChange={(e) => setEditData({ ...editData, discount_amount: e.target.value })}
                  placeholder="0.00"
                  className="h-9"
                  data-testid="edit-discount-amount"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Reason</Label>
                <Input
                  value={editData.discount_reason ?? ''}
                  disabled={!!editData.discount_id}
                  onChange={(e) => setEditData({ ...editData, discount_reason: e.target.value })}
                  placeholder="e.g. Coach referral"
                  className="h-9"
                  data-testid="edit-discount-reason"
                />
              </div>
            </div>

            <div className="space-y-1 pt-2 border-t border-purple-200">
              <Label className="text-xs text-slate-600">
                Override final monthly fee ($)
                <span className="ml-1 text-slate-400">— wins over the discount above</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editData.final_monthly_fee ?? ''}
                onChange={(e) => setEditData({ ...editData, final_monthly_fee: e.target.value })}
                placeholder="Leave blank to use monthly fee minus discount"
                className="h-9"
                data-testid="edit-final-fee"
              />
            </div>

            {renderFinalPricePreview()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={editData.start_date || ''}
                onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                data-testid="edit-start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Next Bill Date</Label>
              <Input
                type="date"
                value={editData.next_billing_date || ''}
                onChange={(e) => setEditData({ ...editData, next_billing_date: e.target.value })}
                data-testid="edit-next-bill-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Last Payment Date</Label>
              <Input
                type="date"
                value={editData.last_payment_date || ''}
                onChange={(e) => setEditData({ ...editData, last_payment_date: e.target.value })}
                data-testid="edit-last-payment-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Payment Amount ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editData.last_payment_amount ?? ''}
                onChange={(e) => setEditData({ ...editData, last_payment_amount: e.target.value })}
                placeholder="Optional"
                data-testid="edit-last-payment-amount-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Payment Due Date</Label>
              <Input
                type="date"
                value={editData.payment_due_date || ''}
                onChange={(e) => setEditData({ ...editData, payment_due_date: e.target.value })}
                data-testid="edit-payment-due-date-input"
              />
            </div>
            {/* iter334bw.3 — Removed the "Prorated 1st Month Amount"
                input. Proration is now auto-derived (from start_date
                at approval + paid amount vs monthly_fee at first-cycle
                billing) and should not be manually overridden after
                the fact. */}
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              placeholder="Any notes about this change..."
              data-testid="edit-notes-input"
            />
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600"
              onClick={onConfirm}
              data-testid="confirm-edit-btn"
            >
              <Check className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
